import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import rehypeRaw from 'rehype-raw';

import ReactPlayer from 'react-player';
import { Spotify } from 'react-spotify-embed';
import { PluggableList } from 'react-markdown/lib/react-markdown';
import Image from './Image';

const IMAGE_MAX_WIDTH = 840;

type FigureProps = { $large?: boolean };
const Figure = styled.figure<FigureProps>`
  display: block;
  max-width: ${(props) => (props.$large ? '100%' : `${IMAGE_MAX_WIDTH}px`)};
  margin: 60px auto;
  & > div {
    width: 100%;
    overflow: hidden;
    border-radius: 6px;
  }
  & img {
    width: 100%;
  }
  & em {
    font-size: 12px;
  }
`;

type BlogPostProps = {
  children: string;
};

export default function BlogPostContent({ children }: BlogPostProps) {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw] as PluggableList}
      linkTarget="_blank"
      components={{
        a: ({ node, ...props }) => {
          if (props?.children?.[0] === 'youtube') {
            return (
              <ReactPlayer
                url={props.href}
                width="100%"
                height={400}
                controls
              />
            );
          }
          if (props?.children?.[0] === 'spotify') {
            return <Spotify link={props.href} width="100%" height={400} />;
          }
          return <a {...props} />;
        },
        img: (props) => {
          return (
            <Zoom zoomMargin={36}>
              <Image
                alt={props.alt || 'Image'}
                src={props.src}
                width={800}
                height={800}
                title={props.alt}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
                placeholder="blur"
              />
            </Zoom>
          );
        },
        p: ({ node, ...props }) => {
          const figure = props.children.find((child) => {
            if (typeof child !== 'object') return false;
            if (!('props' in child)) return false;
            const { props } = child;
            if (props?.node?.tagName === 'img') return true;
            if (props?.children?.[0] === 'youtube') return true;
            if (props?.children?.[0] === 'spotify') return true;
            return false;
          });
          if (figure) {
            const large =
              typeof figure === 'object' &&
              'props' in figure &&
              figure.props.title === 'large';

            return <Figure $large={large} {...props} />;
          }
          return <p {...props} />;
        },
        footer: ({ node, children, ...props }) => {
          const image = node.children.find(
            (d) => d.type === 'element' && d.tagName === 'img'
          );

          const imageSrc = image?.type === 'element' && image.properties?.src;

          const author = node.children.find(
            (d) => d.type === 'element' && d.tagName === 'author'
          );

          const authorName =
            author?.type === 'element' &&
            author.children[0]?.type === 'text' &&
            author.children[0]?.value;

          const position = node.children.find(
            (d) => d.type === 'element' && d.tagName === 'position'
          );

          const positionName =
            position?.type === 'element' &&
            position.children[0]?.type === 'text' &&
            position.children[0]?.value;

          return (
            <footer {...props}>
              {typeof imageSrc === 'string' && (
                <Image
                  src={imageSrc}
                  width={48}
                  height={48}
                  alt={authorName || 'Author'}
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
              )}
              <cite>
                {authorName && <span data-role="author">{authorName}</span>}
                {positionName && (
                  <span data-role="position">{positionName}</span>
                )}
              </cite>
            </footer>
          );
        },
      }}
    >
      {children}
    </ReactMarkdown>
  );
}
